// Helper to extract plain text from rich text blocks
function extractPlainText(blocks: any[]): string {
  if (!Array.isArray(blocks)) return "";
  return blocks
    .map((block) => {
      if (block.type === "paragraph" && Array.isArray(block.children)) {
        return block.children.map((child: any) => child.text || "").join("");
      }
      // Add more block types if needed
      return "";
    })
    .join("\n")
    .trim();
}
import React, { useState, useRef, useEffect } from "react";
import { ImageGallery } from "./ImageGallery";
import type { PostGroup, Page, PostLanding } from "./api";
import { config } from "./config";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "./LanguageContext";
import BlocksRenderer from "./BlocksRenderer";
import { ProgressiveImage } from "./ProgressiveImage";

// Navigation component
type NavbarProps = {
  pages: Page[];
  currentPage: string | null;
  onPageChange: (slug: string) => void;
};

export const Navbar: React.FC<NavbarProps> = ({
  pages,
  currentPage,
  onPageChange,
}) => {
  if (!pages.length) {
    return <div className="text-red-500">No pages found</div>;
  }

  const navRef = useRef<HTMLDivElement>(null);
  const [underline, setUnderline] = useState({ left: 0, width: 0 });

  useEffect(() => {
    // Add a short delay to allow DOM/layout to settle before measuring
    const timeout = setTimeout(() => {
      const idx = pages.findIndex((p) => p.slug === currentPage);
      if (idx === -1 || !navRef.current) {
        setUnderline({ left: 0, width: 0 });
        return;
      }
      const btn = navRef.current.querySelector(
        `#navbar-btn-${pages[idx].slug}`
      ) as HTMLElement;
      if (btn) {
        setUnderline({ left: btn.offsetLeft, width: btn.offsetWidth });
      }
    }, 30); // 30ms delay for layout
    return () => clearTimeout(timeout);
  }, [currentPage, pages]);

  useEffect(() => {
    const handleResize = () => {
      const idx = pages.findIndex((p) => p.slug === currentPage);
      if (idx === -1 || !navRef.current) {
        setUnderline({ left: 0, width: 0 });
        return;
      }
      const btn = navRef.current.querySelector(
        `#navbar-btn-${pages[idx].slug}`
      ) as HTMLElement;
      if (btn) {
        setUnderline({ left: btn.offsetLeft, width: btn.offsetWidth });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentPage, pages]);

  return (
    <nav className="flex justify-between items-center">
      <div className="relative flex h-full gap-4 " ref={navRef}>
        {/* Animated underline */}
        <div
          className="absolute left-0 bottom-0 h-0.5 pointer-events-none"
          style={{ width: "100%" }}
        >
          <div
            className="transition-all duration-300 ease-in-out bg-geraniumSylvaticum rounded-full "
            style={{
              width: `${underline.width}px`,
              transform: `translateX(${underline.left}px)`,
              height: "3px",
            }}
          />
        </div>
        {pages.map((page) => {
          const isActive = currentPage === page.slug;
          return (
            <button
              key={page.documentId}
              id={`navbar-btn-${page.slug}`}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                onPageChange(page.slug);
              }}
              className={
                (isActive
                  ? "text-geraniumSylvaticum "
                  : "hover:text-geraniumSylvaticum text-shadyLeaf ") +
                "relative text-sm py-4  font-medium transition-colors duration-200 bg-transparent border-none outline-none "
              }
              style={{ background: "none" }}
            >
              <span className="relative z-10">{page.title}</span>
            </button>
          );
        })}
      </div>
      <div>
        <LanguageSwitcher />
      </div>
    </nav>
  );
};

// Error component
interface ErrorProps {
  message: string;
}

export const ErrorMessage: React.FC<ErrorProps> = ({ message }) => {
  return (
    <div className="">
      <div className="text-center border border-berryRed/20 bg-berryRed/5 rounded-lg p-6">
        <h3 className="text-lg text-berryRed mb-2 font-medium">Error</h3>
        <p className="text-sunsetSnow">{message}</p>
      </div>
    </div>
  );
};

// Posts list component
interface PostsListProps {
  onPostClick: (slug: string) => void;
  postsGroup: PostGroup[];
  postsLanding: PostLanding[];
  currentPageSlug?: string;
}

export const PostsList: React.FC<PostsListProps> = ({
  postsGroup,
  onPostClick,
  postsLanding,
}) => {
  const { t } = useLanguage();
  // Only show the 'no posts' message if both postsGroup and postsLanding are empty
  const noPosts = !postsGroup || postsGroup.length === 0;
  const noLanding = !postsLanding || postsLanding.length === 0;
  if (noPosts && noLanding) {
    return (
      <div className="text-center py-16">
        <h3 className="text-lg text-shadyLeaf mb-2">{t("posts.noArticles")}</h3>
        <p className="text-sunsetSnow">{t("posts.noContent")}</p>
      </div>
    );
  }

  // Sort posts by date descending (newest first)
  const sortedPosts = Array.isArray(postsGroup) ? [...postsGroup].sort((a, b) => {
    const dateA = a.date ? new Date(a.date) : new Date(0);
    const dateB = b.date ? new Date(b.date) : new Date(0);
    return dateB.getTime() - dateA.getTime();
  }) : [];

  if (!sortedPosts.length) {
    // If there are no posts to show, render nothing (or you could return null)
    return null;
  }

  return (
    <div className="px-6 py-4">
      <div className="space-y-5">
        {sortedPosts.map((post) => {
          return (
            <article
              key={post.documentId}
              className=" bg-white rounded-lg   outline-1 hover:outline-dashed outline-geraniumSylvaticum cursor-pointer group px-6 py-8 -mx-6 "
              onClick={() => onPostClick(post.slug)}
            >
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <div className="text-xs text-sunsetSnow/70 mb-2">
                    {post.date}
                  </div>
                  <h2 className="text-xl font-medium text-shadyLeaf mb-3 group-hover:text-geraniumSylvaticum transition-colors duration-200">
                    {post.title}
                  </h2>
                  {post.article &&
                    post.article.length > 0 &&
                    (() => {
                      const text = extractPlainText(post.article);
                      const maxLen = 160;
                      const preview =
                        text.length > maxLen
                          ? text.slice(0, maxLen) + "..."
                          : text;
                      return (
                        <p className="text-sunsetSnow mb-4 leading-relaxed ">
                          {preview}
                        </p>
                      );
                    })()}
                  <div className="text-sm text-geraniumSylvaticum group-hover:underline font-medium">
                    {t("post.readMore")} →
                  </div>
                </div>
                {post.thumbnail && (
                  <div className=" md:w-48 ">
                    <ProgressiveImage
                      lowQualitySrc={`${config.API_URL.replace("/api", "")}${
                        post.thumbnail.formats?.thumbnail?.url ||
                        post.thumbnail.url
                      }`}
                      highQualitySrc={`${config.API_URL.replace("/api", "")}${
                        post.thumbnail.url
                      }`}
                      alt={post.title}
                      className="w-full h-full aspect-square object-cover rounded-md border border-gray-200"
                    />
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

// Single post component
interface SinglePostProps {
  post: PostGroup;
  onBackClick: () => void;
}

export const SinglePost: React.FC<SinglePostProps> = ({
  post,
  onBackClick,
}) => {
  const { t } = useLanguage();
  // const date = new Date(post.publishedAt || post.createdAt).toLocaleDateString();
  const images = Array.isArray(post.image) ? post.image : [];

  return (
    <div className="bg-white px-6 py-4 rounded-lg">
      <button
        onClick={onBackClick}
        className="text-geraniumSylvaticum hover:text-shadyLeaf mb-8 text-sm font-medium transition-colors duration-200 inline-flex items-center"
      >
        ← {t("post.backToList")}
      </button>

      <article className="space-y-8">
        <header className="border-b border-gray-200 pb-6">
          <div className="text-xs text-sunsetSnow/70 mb-3">{post.date}</div>
          <h1 className="text-3xl font-bold text-shadyLeaf mb-6 leading-tight">
            {post.title}
          </h1>
        </header>

        <div className="flex flex-col gap-8">
          <div className="flex-1 space-y-6">
            {post.article && post.article.length > 0 && (
              <div className="prose max-w-none prose-headings:text-shadyLeaf prose-links:text-geraniumSylvaticum prose-links:no-underline hover:prose-links:underline">
                <BlocksRenderer content={post.article} />
              </div>
            )}
            {images.length > 0 && (
              <ImageGallery images={images} altFallback={post.title} />
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

// Landing Page component
interface LandingPageProps {
  landingPage: PostLanding;
}

export const LandingPageComponent: React.FC<LandingPageProps> = ({
  landingPage,
}) => {
  const images = Array.isArray(landingPage.image) ? landingPage.image : [];
  return (
    <div className="bg-white px-6 pt-4 pb-14  rounded-lg">
      <div className="space-y-8">
        {landingPage.content && landingPage.content.length > 0 && (
          <div className="prose max-w-none prose-headings:text-shadyLeaf prose-links:text-geraniumSylvaticum prose-links:no-underline hover:prose-links:underline">
            <BlocksRenderer content={landingPage.content} />
          </div>
        )}
      </div>
      {images.length > 0 && (
        <ImageGallery images={images} altFallback={"Image"} />
      )}
    </div>
  );
};

// Landing Pages List component
interface LandingPagesListProps {
  landingPages: PostLanding[];
}

export const LandingPagesList: React.FC<LandingPagesListProps> = ({
  landingPages,
}) => {
  if (!landingPages.length) return null;

  return (
    <div className="space-y-0">
      {landingPages.map((landingPage) => (
        <LandingPageComponent
          key={landingPage.documentId}
          landingPage={landingPage}
        />
      ))}
    </div>
  );
};
